import { apiClient } from './api';
import {
  SimpleDataQueryRequest,
  SimpleDataSummarizeRequest,
  SimpleDataFAQRequest,
  SimpleDataResponse,
  FAQResponse,
  SimpleDataSession,
  QueryHistory,
  SummaryHistory,
  FAQHistory,
} from '../models/simpleData';

const SESSION_STORAGE_KEY = 'simpleDataSessions';

export class SimpleDataService {
  // API calls
  static async query(request: SimpleDataQueryRequest): Promise<SimpleDataResponse> {
    const response = await apiClient.post('/api/simple/query', request);
    return response.data;
  }

  static async summarize(request: SimpleDataSummarizeRequest): Promise<SimpleDataResponse> {
    const response = await apiClient.post('/api/simple/summarize', request);
    return response.data;
  }

  static async generateFAQ(request: SimpleDataFAQRequest): Promise<FAQResponse> {
    const response = await apiClient.post('/api/simple/generate/faq', request);
    return response.data;
  }

  // Session management
  static createSession(data: string): SimpleDataSession {
    const session: SimpleDataSession = {
      id: `simple-data-${Date.now()}`,
      data,
      queries: [],
      summaries: [],
      faqs: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(sessionId: string): SimpleDataSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  static getAllSessions(): SimpleDataSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static saveSession(session: SimpleDataSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addQuery(sessionId: string, query: string, result: string): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.queries.push({
        query,
        result,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static addSummary(sessionId: string, type: 'brief' | 'detailed' | 'bullet_points', result: string): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.summaries.push({
        type,
        result,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static addFAQs(sessionId: string, faqs: FAQResponse): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.faqs.push({
        faqs: faqs.faqs,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static exportSession(session: SimpleDataSession): string {
    let content = `# Simple Data Analysis Session\n\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n`;
    content += `**Updated:** ${new Date(session.updatedAt).toLocaleString()}\n\n`;
    
    content += `## Original Data\n\n\`\`\`\n${session.data}\n\`\`\`\n\n`;

    if (session.queries.length > 0) {
      content += `## Queries\n\n`;
      session.queries.forEach((q, index) => {
        content += `### Query ${index + 1} (${new Date(q.timestamp).toLocaleString()})\n\n`;
        content += `**Question:** ${q.query}\n\n`;
        content += `**Answer:** ${q.result}\n\n`;
      });
    }

    if (session.summaries.length > 0) {
      content += `## Summaries\n\n`;
      session.summaries.forEach((s, index) => {
        content += `### Summary ${index + 1} - ${s.type} (${new Date(s.timestamp).toLocaleString()})\n\n`;
        content += `${s.result}\n\n`;
      });
    }

    if (session.faqs.length > 0) {
      content += `## Frequently Asked Questions\n\n`;
      session.faqs.forEach((faqSet, setIndex) => {
        content += `### FAQ Set ${setIndex + 1} (${new Date(faqSet.timestamp).toLocaleString()})\n\n`;
        faqSet.faqs.forEach((faq, index) => {
          content += `**Q${index + 1}:** ${faq.question}\n`;
          content += `**A:** ${faq.answer}\n\n`;
        });
      });
    }

    return content;
  }
}